import angular, { ILocationProvider, IPromise, IQService } from 'angular';
import {
    Ng1StateDeclaration,
    StateParams,
    StateProvider,
    Transition,
    UrlRouterProvider,
} from 'angular-ui-router';

import billingTemplate from '../tn.billing/partials/billing.html';
import cartLayoutTemplate from '../tn.cart/cart-layout.view/cart-layout.html';
import eulaTemplate from '../tn.cart/eula.view/eula.html';
import licensing101Template from '../tn.cart/licensing-101.view/licensing-101.html';
import cartReviewTemplate from '../tn.cart/partials/cartReview.html';
import orderPlacementTemplate from '../tn.cart/partials/orderPlacement.html';
import orderConfirmationTemplate from '../tn.confirmation/partials/orderConfirmation.html';
import errorTemplate from '../tn.ui/error.view/error.html';
import hijackUserTemplate from '../tn.user/hijack-user.view/hijack-user.html'; // TODO: Ask andy about why this is missing.

import accountLicenseFiltersTemplate from '../tn.account/account-license-filters.view/account-license-filters.html';
import accountSettingsTemplate from '../tn.account/account-settings.view/account-settings.html';
import accountYourFontsTemplate from '../tn.account/account-your-fonts.view/account-your-fonts.html';
import orderDetailsTemplate from '../tn.account/orders-detail.view/orders-detail.html';
import { CartRepository } from '../tn.cart/cart-repository.service/cartRepository';
import savedCartDetailSidebarTemplate from '../tn.cart/saved-cart-detail.view/saved-cart-detail-sidebar.html';
import savedCartDetailTemplate from '../tn.cart/saved-cart-detail.view/saved-cart-detail.html';
import savedCartIndexFiltersTemplate from '../tn.cart/saved-carts-index-filters.view/saved-cart-index-filters.html';
import savedCartIndexTemplate from '../tn.cart/saved-carts-index.view/saved-cart-index.html';
import {
    DEFAULT_DESCRIPTION,
    FONT_LIST_PARAMS_STATE_PARAMS,
    FONT_LIST_PARAMS_STATE_PARAMS_URL_PART,
    FontFamilyService,
    FontListParams,
    FontListParamsStateParamsResolver,
    FontStyleService,
    FoundryService,
    IFontFamilyModel,
    OrderService,
    TnErrorCartNotFoundError,
    TnErrorFamilyNameNotFound,
    TnErrorFoundryDataNotProvided,
    TnErrorOrderNotFoundError,
    TnErrorStyleIdNotFound,
    TnErrorStyleNameNotFound,
} from '../tn.common';
import favoritesIndexSidebarTemplate from '../tn.favorites/favorites-index.view/favorites-index-sidebar.html';
import favoritesIndexTemplate from '../tn.favorites/favorites-index.view/favorites-index.html';
import { SeriesService } from '../tn.fonts/SeriesService';
import projectDetailsFilterTemplate from '../tn.projects/projects-detail.view/projects-detail-filters.html';
import projectsDetailTemplate from '../tn.projects/projects-detail.view/projects-detail.html';
import projectsIndexSidebarTemplate from '../tn.projects/projects-index.view/projects-index-sidebar.html';
import projectsIndexTemplate from '../tn.projects/projects-index.view/projects-index.html';
import projectsNewProjectSidebarTemplate from '../tn.projects/projects-new-project.view/projects-new-project-sidebar.html';
import projectsNewProjectTemplate from '../tn.projects/projects-new-project.view/projects-new-project.html';
import { ProjectsRepository } from '../tn.projects/projects-repository.service/projectsRepository';

export interface TNStateDeclaration extends Ng1StateDeclaration {
    // These properties are optional and passed to the view controller
    // TODO: Investigate if these are actually needed. I only see them used in a few places
    title?: string;
    selfData?: unknown;
}

eulaResolver.$inject = ['EulaService', '$stateParams'];
function eulaResolver(EulaService, $stateParams: StateParams) {
    return EulaService.getEula($stateParams.eula_slug);
}

emptyResolver.$inject = ['$q'];
function emptyResolver($q: IQService) {
    return $q.resolve(null);
}

familyFamilyNameResolver.$inject = ['FontFamilyService', '$stateParams'];
function familyFamilyNameResolver(
    familyService: FontFamilyService,
    $stateParams: StateParams
) {
    const param: FontListParams = {
        slug: $stateParams.familyName,
    };
    return familyService.list(param).then((response) => {
        if (response.results.length === 0) {
            throw new TnErrorFamilyNameNotFound($stateParams.familyName);
        }
        return response.results[0];
    });
}

familyFamilyIdResolver.$inject = ['FontFamilyService', '$stateParams'];
function familyFamilyIdResolver(
    familyService: FontFamilyService,
    $stateParams: StateParams
): IPromise<IFontFamilyModel> {
    return familyService.get($stateParams.familyId);
}

styleFamilyNameStyleNameResolver.$inject = [
    'FontFamilyService',
    'FontStyleService',
    '$stateParams',
];
function styleFamilyNameStyleNameResolver(
    familyRepository: FontFamilyService,
    styleService: FontStyleService,
    $stateParams: StateParams
) {
    return familyFamilyNameResolver(familyRepository, $stateParams).then(
        (family) => {
            const params: FontListParams = {
                id: [...family.style, ...family.link_only_styles],
                page_size: 10000,
            };
            return styleService.list(params).then((response) => {
                const style = response.results.find(
                    (s) => s.slug === $stateParams.styleName
                );
                if (!style) {
                    throw new TnErrorStyleNameNotFound(
                        $stateParams.styleName,
                        $stateParams.familyName
                    );
                }
                return style;
            });
        }
    );
}

styleIdResolver.$inject = ['FontStyleService', '$stateParams'];
function styleIdResolver(
    styleService: FontStyleService,
    $stateParams: StateParams
) {
    const styleId = parseInt($stateParams.styleId);

    return styleService
        .list({ id: [styleId], page_size: 10000 })
        .then((styles) => {
            if (!styles.results.length) {
                throw new TnErrorStyleIdNotFound($stateParams.styleId);
            }
            return styles.results[0];
        });
}

fontSeriesResolver.$inject = ['seriesService', '$stateParams'];
function fontSeriesResolver(
    seriesService: SeriesService,
    $stateParams: StateParams
) {
    return seriesService.getSeries($stateParams.seriesName);
}

seriesFamilyIdResolver.$inject = [
    'FontFamilyService',
    'seriesService',
    '$stateParams',
];
function seriesFamilyIdResolver(
    familyService: FontFamilyService,
    seriesService: SeriesService,
    $stateParams: StateParams
) {
    return familyFamilyIdResolver(familyService, $stateParams).then(
        (family) => {
            return seriesService.getSeriesById(family.canonical_series);
        }
    );
}

seriesFamilyNameResolver.$inject = [
    'FontFamilyService',
    'seriesService',
    '$stateParams',
];
function seriesFamilyNameResolver(
    familyService: FontFamilyService,
    seriesService: SeriesService,
    $stateParams: StateParams
) {
    return familyFamilyNameResolver(familyService, $stateParams).then(
        (family) => {
            return seriesService.getSeriesById(family.canonical_series);
        }
    );
}

foundryResolver.$inject = [
    'foundryService',
    'FontFamilyService',
    '$stateParams',
];
function foundryResolver(
    foundryService: FoundryService,
    familyService: FontFamilyService,
    $stateParams: StateParams
) {
    if ($stateParams.foundrySlug) {
        return foundryService.getDjangoFoundryBySlug($stateParams.foundrySlug);
    } else if ($stateParams.foundryEESubdomain) {
        return foundryService.getDjangoFoundryBySubdomain(
            $stateParams.foundryEESubdomain
        );
    } else if ($stateParams.familyName) {
        return familyFamilyNameResolver(familyService, $stateParams).then(
            (family) => {
                const foundryid = family.canonical;
                return foundryService.getDjangoFoundryByDjangoId(foundryid);
            }
        );
    } else {
        throw new TnErrorFoundryDataNotProvided();
    }
}

favoriteResolver.$inject = ['localFavorites', '$stateParams'];
function favoriteResolver(localFavorites, $stateParams: StateParams) {
    return localFavorites.getFavoriteList($stateParams.favoriteId);
}

projectResolver.$inject = ['projectsRepository', '$stateParams'];
function projectResolver(
    projectRepo: ProjectsRepository,
    $stateParams: StateParams
) {
    const id = parseInt($stateParams.projectId);
    return projectRepo.get(id);
}

orderResolver.$inject = ['$stateParams', 'orderService'];
function orderResolver($stateParams: StateParams, orderService: OrderService) {
    if ($stateParams.orderId === null) {
        return {};
    }

    return orderService.getOrder($stateParams.orderId).then(function (order) {
        if (order) return order;
        throw new TnErrorOrderNotFoundError($stateParams.orderId);
    });
}

cartResolver.$inject = ['$stateParams', 'cartRepository'];
function cartResolver(
    $stateParams: StateParams,
    cartRepository: CartRepository
) {
    return cartRepository.getCart($stateParams.cartId).then(function (cart) {
        if (cart) {
            return cart;
        }
        throw new TnErrorCartNotFoundError($stateParams.cartId);
    });
}

TnStoreRouteConfig.$inject = [
    '$stateProvider',
    '$urlRouterProvider',
    '$locationProvider',
    'cmsBaseURl',
];
export function TnStoreRouteConfig(
    $stateProvider: StateProvider,
    $urlRouterProvider: UrlRouterProvider,
    $locationProvider: ILocationProvider,
    cmsBaseURl: string
): void {
    $urlRouterProvider.otherwise(function ($injector, $location) {
        const state = $injector.get('$state');
        state.go('home.error');
        return $location.path();
    });

    $urlRouterProvider.when('/fonts?q', '/fonts/?q');
    $urlRouterProvider.when('/', '/fonts');
    $urlRouterProvider.when('/home', cmsBaseURl);
    $urlRouterProvider.when('/gallery', `${cmsBaseURl}/gallery`);
    $urlRouterProvider.when('/news', `${cmsBaseURl}/news`);

    const states: TNStateDeclaration[] = [];

    states.push({
        name: 'oauthRedirectUri',
        url: '/oidc/callback?code?state?error?error_description',
        views: {
            'main@': {
                component: 'oauthRedirectUri',
            },
        },
        resolve: {
            $title: () => 'OAuth RedirectUri',
        },
    });

    states.push({
        name: 'oauthPostLogoutRedirectUri',
        url: '/oidc/post-logout-redirect?state',
        views: {
            'main@': {
                component: 'oauthPostLogoutRedirectUri',
            },
        },
        resolve: {
            $title: () =>
                'OIDC Connect RP-Initiated Logout 1.0 Post Logout Redirect Resource',
        },
    });

    states.push({
        name: 'hijack',
        url: '/account/hijack/:uid',
        title: 'Hijack Account',
        views: {
            'main@': {
                controller: 'tnHijackUser',
                controllerAs: 'vm',
                template: hijackUserTemplate,
            },
        },
        resolve: {
            $title: () => 'Hijack',
        },
        data: {
            requireAuthorized: true,
        },
    });

    //This state is an abstract state that all other states inherit from
    states.push({
        name: 'home',
        url: '/',
        views: {
            header: {
                component: 'tnNavBar',
            },
        },
        resolve: {
            $title: () => 'Type Network',
            $description: () => DEFAULT_DESCRIPTION,
        },
    });

    states.push({
        name: 'home.error',
        views: {
            'main@': {
                controller: 'tnError',
                controllerAs: 'vm',
                template: errorTemplate,
            },
        },
        params: {
            error: undefined,
        },
        resolve: {
            $title: () => 'Error',
        },
    });

    // /cart
    states.push({
        name: 'home.checkout',
        url: 'cart',
        abstract: true,
        views: {
            'sidebar@': {
                template: licensing101Template,
            },
            'main@': {
                template: cartLayoutTemplate,
            },
        },
        data: {
            sidebar: {
                type: '_max',
                toggle: 'licensing information',
            },
        },
        resolve: {
            $title: () => 'Checkout',
            $description: () =>
                'Complete your Type Network order, with a ' +
                'curated selection of typefaces from quality ' +
                'foundries for use across desktop, web, apps, and more',
        },
    });

    // /cart
    states.push({
        name: 'home.checkout.review',
        url: '',
        template: cartReviewTemplate,
        controller: 'tn.cart.controller.cartReview',
        controllerAs: 'vm',
        resolve: {
            $title: () => 'Review',
            $description: () =>
                'Your Type Network cart, with a ' +
                'curated selection of typefaces from quality ' +
                'foundries for use across desktop, web, apps, and more',
        },
    });

    // /cart/billing
    states.push({
        name: 'home.checkout.billing',
        url: '/billing',
        template: billingTemplate,
        controller: 'tn.billing.controller.billingInfo',
        params: {
            error: { value: null },
        },
        resolve: {
            $title: () => 'Billing',
        },
    });
    // /cart/order
    states.push({
        name: 'home.checkout.order',
        url: '/order',
        template: orderPlacementTemplate,
        controller: 'tn.cart.controller.cartReview',
        controllerAs: 'vm',
        data: {
            requireAuthorized: true,
        },
        resolve: {
            $title: () => 'Place Order',
        },
    });
    // /cart/confirmation/:orderId
    states.push({
        name: 'home.checkout.confirmation',
        url: '/confirmation/:orderId',
        template: orderConfirmationTemplate,
        controller: 'tn.confirmation.controller.orderConfirmation',
        controllerAs: 'vm',
        params: {
            orderId: null,
        },
        resolve: {
            order: orderResolver,
            $title: () => 'Confirmation',
        },
        data: {
            requireAuthorized: true,
        },
    });
    // cart/eula?types=licensetypes&cart=:cartId
    states.push({
        name: 'home.eula',
        url: 'cart/eula?types?cart',
        resolve: {
            foundry: emptyResolver,
            $title: [() => 'EULA'],
        },
        params: {
            types: {
                array: true,
                value: [],
            },
        },
        views: {
            'main@': {
                template: eulaTemplate,
                controller: 'tnEula',
                controllerAs: 'vm',
            },
        },
    });

    states.push({
        name: 'home.eula.foundry',
        url: '/:eula_slug',
        resolve: {
            $title: [() => 'EULA'],
            eula: eulaResolver,
        },
        views: {
            'main@': {
                template: eulaTemplate,
                controller: 'tnEula',
                controllerAs: 'vm',
            },
        },
    });

    // /fonts
    states.push({
        name: 'home.fonts',
        url: 'fonts/',
        abstract: true,
        views: {
            'main@': {
                template: '<ui-view />',
            },
        },
        data: {
            sidebar: {
                type: '_view_toolbar_fixed',
                toggle: 'filters',
            },
        },
    });

    // /fonts
    states.push({
        name: 'home.fonts.listing',
        url: `${FONT_LIST_PARAMS_STATE_PARAMS_URL_PART}`,
        params: {
            ...FONT_LIST_PARAMS_STATE_PARAMS,
        },
        resolve: {
            sort: [
                '$stateParams',
                function ($stateParams) {
                    return $stateParams.sort || 'name';
                },
            ],
            $title: [() => 'Buy Fonts'],
            filters: ['$stateParams', FontListParamsStateParamsResolver],
        },
        selfData: {
            breadcrumbTitle: 'All Fonts',
        },
        views: {
            'main@': 'catalogPage',
            'sidebar@': 'catalogPageSidebar',
        },
    });

    // fonts/lookup
    states.push({
        name: 'home.fonts.lookup',
        url: 'lookup/',
        abstract: true,
    });

    // /fonts/lookup/:familyId
    states.push({
        // redirect fonts/lookup/:familyId to fonts/:familyName
        name: 'home.fonts.lookup.family',
        url: ':familyId',
        resolve: {
            family: familyFamilyIdResolver,
            style: emptyResolver,
            series: seriesFamilyIdResolver,
        },
        onEnter: [
            'family',
            '$transition$',
            function (family, $transition$: Transition) {
                // Check that we're not looking up a style, otherwise we'll redirect to the family page
                if (
                    $transition$.$to().name === 'home.fonts.lookup.family.style'
                ) {
                    return;
                }

                // We've resolved the family, now redirect to it
                return $transition$.router.stateService.target(
                    'home.foundry.fonts.styles',
                    family.getSrefParams()
                );
            },
        ],
    });

    // /fonts/lookup/:familyId/:styleId
    states.push({
        // redirect fonts/lookup/:familyId/:styleId to fonts/:familyName/:styleName
        name: 'home.fonts.lookup.family.style',
        url: '/:styleId',
        resolve: {
            family: emptyResolver,
            style: styleIdResolver,
            foundry: emptyResolver,
        },
        params: {
            familyId: null,
        },
        onEnter: [
            'style',
            '$transition$',
            function (style, $transition$: Transition) {
                // We've resolved the family and style, now redirect to it
                return $transition$.router.stateService.target(
                    'home.foundry.fonts.styles.style',
                    style.getSrefParams()
                );
            },
        ],
    });

    // /foundry/:foundryEESubdomain
    states.push({
        name: 'home.foundry',
        abstract: true,
        url: 'foundry/:foundryEESubdomain/',
        resolve: {
            foundry: foundryResolver,
            $title: ['foundry', (foundry) => `${foundry.name} · Buy Fonts`],
            $description: [
                'foundry',
                (foundry) =>
                    `${foundry.name}'s Fonts are available from Type Network ` +
                    'for desktop, web, app, and ePub licensing. TypeNetwork.com ' +
                    'has quality fonts to try and buy.',
            ],
        },
        views: {
            'header@': 'tnNavBar',
        },
    });

    // /foundry/:foundryEESubdomain/fonts
    states.push({
        name: 'home.foundry.fonts',
        url: 'fonts/',
        abstract: true,
    });

    // /foundry/:foundryEESubdomain/fonts
    states.push({
        name: 'home.foundry.fonts.listing',
        url: `${FONT_LIST_PARAMS_STATE_PARAMS_URL_PART}`,
        params: {
            ...FONT_LIST_PARAMS_STATE_PARAMS,
        },
        resolve: {
            sort: [
                '$stateParams',
                ($stateParams) => $stateParams.sort || 'name',
            ],
            filters: ['$stateParams', FontListParamsStateParamsResolver],
        },
        views: {
            'main@': 'catalogPage',
            'sidebar@': 'catalogPageSidebar',
        },
        data: {
            bodyClasses: ['template-fonts', 'template-fonts-listing'],
            sidebar: {
                type: '_view_toolbar_fixed',
                toggle: 'filters',
            },
        },
    });

    // /foundry/:foundryEESubdomain/fonts/:familyName
    states.push({
        name: 'home.foundry.fonts.styles',
        url: `:familyName${FONT_LIST_PARAMS_STATE_PARAMS_URL_PART}`,
        views: {
            'sidebar@': 'familyPageSidebar',
            'main@': 'familyPage',
        },
        resolve: {
            filters: ['$stateParams', FontListParamsStateParamsResolver],
            family: familyFamilyNameResolver,
            style: emptyResolver,
            foundry: foundryResolver,
            series: seriesFamilyNameResolver,
            $title: [
                'family',
                'series',
                (family, series) => {
                    if (series) {
                        return `${series.name}`;
                    }
                    if (family) {
                        return `${family.name}`;
                    }
                    return '';
                },
            ],
            $description: [
                'foundry',
                'family',
                'series',
                (foundry, family, series) => {
                    let predicate = 'Fonts';
                    if (series) {
                        predicate = series.name;
                    } else if (family) {
                        predicate = family.name;
                    }
                    const result =
                        predicate +
                        ` by ${foundry.name} is available from ` +
                        'Type Network for desktop, web, app, and ePub licensing. ' +
                        'TypeNetwork.com has quality fonts to try and buy.';
                    return result;
                },
            ],
        },
        data: {
            sidebar: {
                type: '_view_toolbar_full_header',
                toggle: 'filters',
            },
            bodyClasses: ['template-fonts', 'template-fonts-styles'],
        },
    });

    // /foundry/:foundryEESubdomain/fonts/:familyName/:styleName
    states.push({
        name: 'home.foundry.fonts.styles.style',
        url: '/:styleName',
        views: {
            'sidebar@': 'stylePageSidebar',
            'main@': 'stylePage',
        },
        resolve: {
            family: familyFamilyNameResolver,
            style: styleFamilyNameStyleNameResolver,
            foundry: foundryResolver,
            $title: [
                'style',
                (style) => {
                    return `${style.style_name}`;
                },
            ],
            $description: [
                'style',
                'foundry',
                (style, foundry) =>
                    `${style.name} by ${foundry.name} is available from ` +
                    'Type Network for desktop, web, app, and ePub licensing. ' +
                    'TypeNetwork.com has quality fonts to try and buy.',
            ],
        },
        data: {
            sidebar: {
                type: '_main_header_fixed',
                toggle: 'sidebar',
            },
            bodyClasses: [
                'template-fonts',
                'template-fonts-styles',
                'template-fonts-styles-style',
            ],
        },
    });

    states.push({
        name: 'home.foundry.series',
        url: `series/:seriesName?family${FONT_LIST_PARAMS_STATE_PARAMS_URL_PART}`,
        views: {
            'main@': 'seriesPage',
            'sidebar@': 'seriesPageSidebar',
        },
        params: {
            ...FONT_LIST_PARAMS_STATE_PARAMS,
            family: {
                // comma separated list of family slugs
                dynamic: true,
                value: undefined,
                squash: true,
            },
        },
        data: {
            sidebar: {
                type: '_view_toolbar_full_header',
                toggle: 'filters',
            },
        },
        resolve: {
            foundry: foundryResolver,
            series: fontSeriesResolver,
            filters: ['$stateParams', FontListParamsStateParamsResolver],
            $title: ['series', (series) => `${series.name}`],
            $description: [
                'foundry',
                'series',
                (foundry, series) =>
                    `${series.name} by ${foundry.name} is available from ` +
                    'Type Network for desktop, web, app, and ePub licensing. ' +
                    'TypeNetwork.com has quality fonts to try and buy.',
            ],
        },
    });

    states.push({
        name: 'home.account',
        url: 'account',
        resolve: {
            $title: [() => 'Account'],
            $description: () => 'Your Type Network account',
        },
        data: {
            requireAuthorized: true,
            sidebar: {
                type: '_main_header_fixed',
                toggle: 'account information',
            },
            bodyClasses: ['template-account-overview'],
        },
        redirectTo: 'home.account.orders',
    });

    states.push({
        name: 'home.account.settings',
        url: '/settings',
        resolve: {
            $title: [() => 'Settings'],
        },
        views: {
            'main@': {
                template: accountSettingsTemplate,
                controller: 'tnAccountSettings',
                controllerAs: 'vm',
            },
        },
    });

    states.push({
        name: 'home.account.licenses',
        url: '/licenses',
        views: {
            'main@': {
                template: accountYourFontsTemplate,
                controller: 'tnAccountYourFonts',
                controllerAs: 'vm',
            },
            'sidebar@': {
                template: accountLicenseFiltersTemplate,
                controller: 'tnAccountLicenseFilters',
                controllerAs: 'vm',
            },
        },
        params: {
            orderId: null,
        },
        data: {
            bodyClasses: ['template-account-your-fonts'],
        },
        resolve: {
            order: orderResolver,
            $title: [() => 'Your Fonts'],
        },
    });

    states.push({
        name: 'home.account.orders',
        url: '/orders',
        views: {
            'main@': 'accountOrdersPage',
            'sidebar@': 'accountOrdersSidebar',
        },
        resolve: {
            $title: [() => 'Orders'],
        },
    });

    states.push({
        name: 'home.account.orders.order',
        url: '/:orderId',
        views: {
            'main@': {
                template: orderDetailsTemplate,
                controller: 'tnOrdersDetail',
                controllerAs: 'vm',
            },
            'sidebar@': {
                template: accountLicenseFiltersTemplate,
                controller: 'tnAccountLicenseFilters',
                controllerAs: 'vm',
            },
        },
        resolve: {
            order: orderResolver,
            $title: ['order', (order) => `Order #${order.id}`],
        },
    });

    states.push({
        name: 'home.account.projects',
        url: '/projects',
        views: {
            'main@': {
                template: projectsIndexTemplate,
                controller: 'tnProjectsIndex',
                controllerAs: 'vm',
            },
            'sidebar@': {
                template: projectsIndexSidebarTemplate,
                controller: 'tnProjectsIndexSidebar',
                controllerAs: 'vm',
            },
        },
        resolve: {
            $title: [() => 'Web projects'],
        },
    });

    states.push({
        name: 'home.account.projects.newProject',
        url: '/new',
        views: {
            'main@': {
                template: projectsNewProjectTemplate,
                controller: 'tnProjectsNewProject',
                controllerAs: 'vm',
            },
            'sidebar@': {
                template: projectsNewProjectSidebarTemplate,
            },
        },
        params: {
            orderId: null,
        },
        resolve: {
            order: orderResolver,
            $title: [() => 'New Project'],
        },
    });

    states.push({
        name: 'home.account.projects.project',
        url: '/:projectId',
        views: {
            'main@': {
                template: projectsDetailTemplate,
                controller: 'tnProjectsDetail',
                controllerAs: 'vm',
            },
            'sidebar@': {
                template: projectDetailsFilterTemplate,
                controller: 'tnProjectDetailFilters',
                controllerAs: 'vm',
            },
        },
        resolve: {
            project: projectResolver,
            $title: ['project', (project) => `${project.name}`],
        },
    });

    states.push({
        name: 'home.account.favorites',
        url: '/favorites',
        views: {
            'main@': {
                template: favoritesIndexTemplate,
                controller: 'tnFavoritesIndex',
                controllerAs: 'vm',
            },
            'sidebar@': {
                template: favoritesIndexSidebarTemplate,
                controller: 'tnFavoritesIndexSidebar',
                controllerAs: 'vm',
            },
        },
        resolve: {
            $title: [() => 'Favorites'],
        },
    });

    states.push({
        name: 'home.account.favorites.favorite',
        url: `/:favoriteId${FONT_LIST_PARAMS_STATE_PARAMS_URL_PART}`,
        params: {
            ...FONT_LIST_PARAMS_STATE_PARAMS,
        },
        views: {
            'main@': 'favoriteDetailPage',
            'sidebar@': 'favoritesDetailSidebar',
        },
        resolve: {
            favorite: favoriteResolver,
            filters: ['$stateParams', FontListParamsStateParamsResolver],
            $title: ['favorite', (favorite) => `${favorite.name}`],
        },
        data: {
            requireAuthorized: false,
            sidebar: {
                type: '_main_header_fixed',
                toggle: 'sidebar',
            },
        },
    });

    states.push({
        name: 'home.account.carts',
        url: '/carts',
        views: {
            'main@': {
                template: savedCartIndexTemplate,
                controller: 'tnSavedCartsIndex',
                controllerAs: 'vm',
            },
            'sidebar@': {
                template: savedCartIndexFiltersTemplate,
                controller: 'tnSavedCartsIndexFilters',
                controllerAs: 'vm',
            },
        },
        data: {
            sidebar: {
                type: '_main_header_fixed',
                toggle: 'cart filters',
            },
            requireAuthorized: true,
        },
        resolve: {
            $title: [() => 'Saved carts'],
        },
    });
    states.push({
        name: 'home.account.carts.cart',
        url: '/:cartId',
        views: {
            'main@': {
                template: savedCartDetailTemplate,
                controller: 'tnSavedCartDetail',
                controllerAs: 'vm',
            },
            'sidebar@': {
                template: savedCartDetailSidebarTemplate,
                controller: 'tnSavedCartDetailSidebar',
                controllerAs: 'vm',
            },
        },
        data: {
            requireAuthorized: false,
        },
        resolve: {
            cart: cartResolver,
            $title: ['cart', (cart) => `${cart.title}`],
        },
    });

    angular.forEach(states, function (state) {
        $stateProvider.state(state);
    });

    $locationProvider.html5Mode({
        enabled: true,
        requireBase: false,
        rewriteLinks: false,
    });
}
